'use client';

import { useEffect, useCallback, useRef } from 'react';
import { POST_MESSAGE_TYPE } from './constants';
import { validateSource, validateOrigin } from './validate';

export interface UsePostMessageProps {
    listeningOnIframe?: boolean;
}

export interface PostMessageData<T> {
    eventType: POST_MESSAGE_TYPE;
    data: T;
}

export interface PostMessageEvent extends MessageEvent {
    data: string;
}

type PostEventKeyType = POST_MESSAGE_TYPE;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PostEventValueType = (data: any) => void;
type PostEventCallbackMap = Record<PostEventKeyType, Array<PostEventValueType>>;

type SubscribePostMessageEvent = {
    eventType: POST_MESSAGE_TYPE;
    callback: PostEventValueType;
    // 預設不允許 postMessage 訂閱的事件疊加，不容許多組 callback 訂閱同一組 eventType
    // override 是 false 的話會將 callback 推入 eventType 對應的 callback list 一起執行。
    override?: boolean;
};

const ignorePrintingEvent = [
    POST_MESSAGE_TYPE.INITIALIZE_SHOP_PAGE_LAYOUT,
    POST_MESSAGE_TYPE.SYNC_DATA,
];

export function usePostMessage(options: UsePostMessageProps = {}) {
    const { listeningOnIframe = false } = options;

    const eventCallbackRef = useRef<PostEventCallbackMap>({} as PostEventCallbackMap);

    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    function subscribePostMessageEvent({
        eventType,
        callback,
        override = true,
    }: SubscribePostMessageEvent) {
        eventCallbackRef.current = {
            ...eventCallbackRef.current,
            [eventType]: override
                ? [callback]
                : [...(eventCallbackRef.current[eventType] || []), callback],
        };
    }

    function getSubscribedPostMessageEventCallbackList(eventType: POST_MESSAGE_TYPE) {
        if (!eventCallbackRef.current || !eventCallbackRef.current[eventType]) {
            console.error('Event type not registered', { eventType });

            return [];
        }

        const callbackList = eventCallbackRef.current[eventType];

        return callbackList;
    }

    function registerIframe(_iframe: HTMLIFrameElement) {
        if (iframeRef.current && iframeRef.current.src === _iframe.src) {
            return;
        }

        iframeRef.current = _iframe;
    }

    const postMessage = useCallback(
        <CustomMessage>(data: PostMessageData<CustomMessage>) => {
            if (typeof window === 'undefined') {
                return;
            }

            try {
                const targetOrigin = window.location.origin;

                const stringifyData = JSON.stringify(data);

                if (listeningOnIframe) {
                    window.parent.postMessage(stringifyData, targetOrigin);
                } else if (iframeRef.current && iframeRef.current.contentWindow) {
                    iframeRef.current.contentWindow.postMessage(
                        stringifyData,
                        targetOrigin
                    );
                }

                if (process.env.NEXT_PUBLIC_MODE === 'development') {
                    if (!ignorePrintingEvent.includes(data.eventType)) {
                        console.log(
                            '%c Post message from %s \n%O',
                            'color:red; font-size: 12px; background-color: yellow',
                            listeningOnIframe ? 'iframe' : 'parent window',
                            stringifyData
                        );
                    }
                }
            } catch (error) {
                throw new Error('Can sent the message to the target origin.');
            }
        },
        [listeningOnIframe, iframeRef]
    );

    useEffect(() => {
        function receiveMessage<CustomMessage>(event: PostMessageEvent) {
            const isSameSource = validateSource(event, iframeRef.current);

            const isSameOrigin = validateOrigin(event, iframeRef.current);

            if (!isSameSource || !isSameOrigin || typeof event.data !== 'string') {
                return;
            }

            try {
                const { eventType, data: receiveData } = JSON.parse(
                    event.data
                ) as PostMessageData<CustomMessage>;

                const eventNotAllow =
                    !Object.values(POST_MESSAGE_TYPE).includes(eventType);

                if (eventNotAllow) {
                    return;
                }

                if (process.env.NEXT_PUBLIC_MODE === 'development') {
                    if (!ignorePrintingEvent.includes(eventType)) {
                        console.log(
                            '%c Received message from %s \n%O',
                            'color:red; font-size: 12px; background-color: yellow',
                            listeningOnIframe ? 'parent window' : 'iframe',
                            event.data
                        );
                    }
                }

                const callbackList = getSubscribedPostMessageEventCallbackList(eventType);

                const parsedData = receiveData;

                if (callbackList.length) {
                    callbackList.forEach((callback) => {
                        callback(parsedData);
                    });
                }
            } catch (error) {
                if (error instanceof TypeError) {
                    if (process.env.NEXT_PUBLIC_MODE === 'development') {
                        console.error('Invalid data format', { error });
                    }

                    throw new Error('Invalid data format');
                }

                throw new Error('No callback registered for event type');
            }
        }

        window.addEventListener('message', receiveMessage);

        return () => {
            window.removeEventListener('message', receiveMessage);
        };
    }, [iframeRef, listeningOnIframe]);

    function getRegistryIframe() {
        return iframeRef.current;
    }

    return {
        getRegistryIframe,
        registerIframe,
        subscribePostMessageEvent,
        postMessage,
    };
}

export type UsePostMessageReturn = ReturnType<typeof usePostMessage>;
